// Tryb — Facilitator Studio.

import { useCallback, useEffect, useState } from "react";
import { StudioProvider } from "./lib/StudioProvider.jsx";
import { useStudio } from "./lib/studioContext.js";
import Login from "./screens/Login.jsx";
import Onboarding from "./screens/Onboarding.jsx";
import Dashboard from "./screens/Dashboard.jsx";
import Wizard from "./screens/Wizard.jsx";
import CoverChooser from "./components/CoverChooser.jsx";
import Toaster from "./components/Toaster.jsx";
import { api } from "./lib/api.js";

export default function App() {
  // undefined while we're still asking the server who this is.
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api
      .me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, []);

  if (user === undefined) return <Splash>Opening your studio…</Splash>;
  if (!user) return <Login onSignedIn={setUser} />;

  return (
    // Keying on the user id throws away the previous account's studio state on
    // a sign-out and back in, rather than briefly showing someone else's.
    <StudioProvider key={user.id}>
      <Studio user={user} onSignOut={signOut} />
      <Toaster />
    </StudioProvider>
  );
}

function Studio({ user, onSignOut }) {
  const { ready, loadError, state, showToast, refresh } = useStudio();

  const [page, setPage] = useState("home");
  const [wizard, setWizard] = useState(null);
  const [coverOpen, setCoverOpen] = useState(false);

  const openCover = useCallback(() => setCoverOpen(true), []);

  const onPublished = useCallback(
    ({ page: next, title, desc }) => {
      setWizard(null);
      setPage(next);
      showToast(title, desc);
    },
    [showToast],
  );

  if (!ready) return <Splash>Opening your studio…</Splash>;

  if (loadError) {
    return (
      <Splash>
        <strong style={{ display: "block", marginBottom: 6 }}>Can’t reach the studio API.</strong>
        <span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          Start it with <code>npm run dev</code>, then reload. ({loadError.message})
        </span>
      </Splash>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--surface-page)", fontFamily: "var(--font-body)", color: "var(--text-primary)", display: "flex" }}>
      {state.onboarded ? (
        <Dashboard
          page={page}
          onNavigate={setPage}
          onStartListing={() => setWizard("listing")}
          onStartVenue={() => setWizard("venue")}
          onEditListing={() => setWizard("listing")}
          onOpenCover={openCover}
          account={user}
          onSignOut={onSignOut}
        />
      ) : (
        <Onboarding onEnterStudio={setPage} />
      )}

      {wizard && <Wizard mode={wizard} onClose={() => setWizard(null)} onOpenCover={openCover} onPublished={onPublished} />}

      {coverOpen && <CoverChooser onClose={() => setCoverOpen(false)} />}

    </div>
  );
}

function Splash({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        background: "var(--surface-page)",
        fontFamily: "var(--font-body)",
        color: "var(--text-muted)",
      }}
    >
      <div style={{ maxWidth: 420 }}>{children}</div>
    </div>
  );
}
